import { Component, EventEmitter, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd } from '@angular/router';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/_services/auth.service';
import { NotificationService } from 'src/app/_services/notification.service';
import { ProfileService } from 'src/app/_services/profile.service';
import { SectionService } from 'src/app/_services/section.service';
import { SidebarService } from 'src/app/_services/sidenav.service';
import { environment } from 'src/environments/environment';
import { BaseComponent } from '../components/base/base.component';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css']
})
export class SidebarComponent extends BaseComponent {
  private sectionsStatus: { [key: string]: boolean } = {};
  isAdmin: boolean;
  sidenav!: HTMLElement;
  toggler!: HTMLElement;
  logo: any;
  logoMonitoraggio: any;
  sidenavClosed$!: Observable<boolean>;
  @Output() toggleSidebar: EventEmitter<void> = new EventEmitter<void>();
  isSidenavClosed: boolean = false;
  profileData: any;
  hobbyNotification: any;
  role: any;
  notification: any;
  navigationSubscription: any;
  path: string;
  mediaQueryList: MediaQueryList;
  checkMonitoraggio: boolean;
  showToggleSection: boolean = false;

  destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly authService: AuthService,
    private readonly sanitizer: DomSanitizer,
    public profileService: ProfileService,
    private readonly sidebarService: SidebarService,
    private readonly notificationService: NotificationService,
    private readonly sectionsService: SectionService
  ) {
    super();
    this.profileService.getImg();
    if (environment.production) {
      this.logo = 'assets/images/loghi/newLogoExt.svg';
    } else if (environment.local) {
      this.logo = 'assets/images/loghi/newLogoExtLocal.svg';
    } else {
      this.logo = 'assets/images/loghi/newLogoExtDev.svg';
    }
    if (environment.production) {
      this.logoMonitoraggio = 'assets/images/loghi/monitoraggio.svg';
    } else if (environment.local) {
      this.logoMonitoraggio = 'assets/images/loghi/monitoraggioLocal.svg';
    } else {
      this.logoMonitoraggio = 'assets/images/loghi/monitoraggioDev.svg';
    }
  }

  async ngOnInit(): Promise<void> {
    this.isAdmin = await this.profileService.isAdmin();

    this.profileService.getImg().subscribe({
      next: (value: any) => {
        if (value != null) this.getSantizeUrl(value);
      }
    });

    this.profileService.userLogged.subscribe({
      next: value => {
        this.profileData = value;
      }
    });

    this.sectionsService.getSectionsStatus().subscribe(value => {
      this.sectionsStatus = value;
    });

    this.getSantizeUrl(this.profileData?.filestorage?.path);
    this.sidenav = document.querySelector('.sidebar')!;
    this.toggler = document.querySelector('.toggle')!;
    this.sidenavClosed$ = this.sidebarService.getSidenavClosed();
    this.navigationSubscription =
      this.notificationService.notification.subscribe(notificationValues => {
        this.notification = notificationValues[0];
        this.hobbyNotification = notificationValues[1];
      });

    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.checkMonitoraggio = this.isMonitoraggioActive();
      }
    });
    this.checkMonitoraggio = this.isMonitoraggioActive();

    this.sidenavClosed$.subscribe(isSidebarClosed => {
      this.isSidenavClosed = isSidebarClosed;
      if (this.isSidenavClosed) {
        this.sidenav.classList.add('close');
        this.toggler.classList.remove('left-oriented');
        this.toggler.classList.add('right-oriented');
      } else {
        this.sidenav.classList.remove('close');
        this.toggler.classList.remove('right-oriented');
        this.toggler.classList.add('left-oriented');
      }
    });
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.sidenav = document.querySelector('.sidebar')!;
    this.sidenavClosed$ = this.sidebarService.getSidenavClosed();
    this.sidenavClosed$.subscribe(isClosed => {
      if (isClosed) {
        this.isSidenavClosed = isClosed;
        this.sidenav.classList.add('close');
        this.toggler.classList.remove('left-oriented');
        this.toggler.classList.add('right-oriented');
      } else {
        this.isSidenavClosed = isClosed;
        this.sidenav.classList.remove('close');
        this.toggler.classList.remove('right-oriented');
        this.toggler.classList.add('left-oriented');
      }
    });
  }

  /**
   * funzione che serve per capire se una sezione è attiva oppure no
   * @param section nome della sezione
   * @returns boolean
   */
  isSectionActive(section: string): boolean {
    return this.sectionsStatus[section];
  }

  /**
   * funzione che serve per capire se l'utente loggato può accedere sempre alla sezione
   * @param section nome della sezione
   * @returns boolean
   */
  canAccessSection(section: string): boolean {
    if (this.isAdmin) {
      return true;
    }
    return this.sectionsService.isSectionEnabled(section);
  }

  /**
   * funzione che serve per settare se una sezione è accessibile da tutti oppure no
   * @param section nome della sezione
   * @param enabled boolean
   */
  setAccessSection(section: string, enabled: boolean) {
    this.sectionsService
      .toggleSection(section, enabled)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.getIndexSection();
          this.responseToast('success', res.message);
        },
        error: (err: any) => {
          this.responseToast('error', err.error.error);
        }
      });
  }

  /**
   * funzione per richiamare API per le sezioni
   */
  async getIndexSection() {
    const res: any = await firstValueFrom(this.sectionsService.loadSections());
    this.sectionsStatus = res.Sections;
  }

  logout(): void {
    this.authService.logout().subscribe(res => {
      this.profileService.userLogged.next(null);
      this.profileService.profileLoaded.next(false);
      this.router.navigate(['/login']);
    });
  }

  toggle(): void {
    // Ottieni le sidebar principale e di monitoraggio
    const mainSidebar = document.querySelector('.sidebar:not(.monitoraggio)');
    const monitoraggioSidebar = document.querySelector('.sidebar.monitoraggio');
    const currentUrl = this.router.url;
    if (currentUrl.includes('calendario')) {
      return;
    }

    // Inverti lo stato di entrambe le sidebar
    if (mainSidebar) {
      mainSidebar.classList.toggle('close');
    }
    if (monitoraggioSidebar) {
      monitoraggioSidebar.classList.toggle('close');
    }

    // Imposta lo stato di chiusura in base alla sidebar attualmente visualizzata
    this.isSidenavClosed = !this.isSidenavClosed;

    // Emetti l'evento per informare del cambio di stato della navbar
    this.toggleSidebar.emit();

    // Passa lo stato aggiornato alla service per la navbar
    this.sidebarService.toggleSidenav(this.isSidenavClosed);

    // Aggiorna lo stile del toggler
    if (!this.isSidenavClosed) {
      this.toggler.classList.remove('right-oriented');
      this.toggler.classList.add('left-oriented');
    } else {
      this.toggler.classList.remove('left-oriented');
      this.toggler.classList.add('right-oriented');
    }
  }

  showCondivisione() {
    this.router.navigate(['condivisione']);
  }

  getSantizeUrl(url: string) {
    this.path = environment.file + url;
    return this.sanitizer.bypassSecurityTrustUrl(this.path);
  }

  isMonitoraggioActive(): boolean {
    return window.location.pathname.includes('monitoraggio');
  }

  functionShowToggleSection() {
    this.showToggleSection = !this.showToggleSection;
  }
}
